import React, { useState, useEffect } from 'react';
import { api } from '../../services/authService';

const CommunicationEndpointsProvider = ({ 
  children, 
  accountId, 
  campaignId, 
  channel,
  activeTab,
  includeUnassigned = false // New prop to optionally include endpoints not assigned to any campaign
}) => {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEndpoints = async () => {
      if (activeTab === 1 && accountId && channel) {
        setLoading(true);
        setError(null);
        try {
          // Build query parameters
          const params = {
            account_id: accountId,
            channel: channel
          };

          // Only add campaign_id filter if it's provided and we don't want unassigned endpoints
          if (campaignId && !includeUnassigned) {
            params.campaign_id = campaignId;
          }

          const response = await api.get('/communications/contact-endpoints/', {
            params
          });

          // If we're filtering by campaign, the backend should return only endpoints assigned to that campaign
          // If we want to include unassigned endpoints, we might need to make an additional call
          let filteredEndpoints = response.data;

          if (includeUnassigned && campaignId) {
            // If we want to include unassigned endpoints, we need to fetch all endpoints
            // and then filter them on the frontend
            const allEndpointsResponse = await api.get('/communications/contact-endpoints/', {
              params: {
                account_id: accountId,
                channel: channel
              }
            });

            // Filter endpoints that are either assigned to the campaign OR not assigned to any campaign
            filteredEndpoints = allEndpointsResponse.data.filter(endpoint => {
              const isAssignedToCampaign = endpoint.campaign_ids && endpoint.campaign_ids.includes(campaignId);
              const isUnassigned = !endpoint.campaign_ids || endpoint.campaign_ids.length === 0;
              return isAssignedToCampaign || isUnassigned;
            });
          }

          setEndpoints(filteredEndpoints);
        } catch (err) {
          console.error('Error fetching communication endpoints:', err);
          setError('Failed to fetch communication endpoints');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchEndpoints();
  }, [activeTab, accountId, campaignId, channel, includeUnassigned]);

  // Helper function to check if an endpoint is assigned to a specific campaign
  const isEndpointAssignedToCampaign = (endpoint, targetCampaignId) => {
    return endpoint.campaign_ids && endpoint.campaign_ids.includes(targetCampaignId);
  };

  // Helper function to get campaign assignment info for an endpoint
  const getCampaignAssignmentInfo = (endpoint, targetCampaignId) => {
    if (!endpoint.campaigns) return null;
    
    const campaignInfo = endpoint.campaigns.find(campaign => campaign.id === targetCampaignId);
    return campaignInfo ? {
      name: campaignInfo.name,
      assignedAt: campaignInfo.assigned_at
    } : null;
  };

  return children({ 
    endpoints, 
    loading, 
    error,
    isEndpointAssignedToCampaign,
    getCampaignAssignmentInfo
  });
};

export default CommunicationEndpointsProvider; 